import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { sanitizeReturnTo } from '../lib/returnTo';
import { HaurakiLogo } from '../components/HaurakiLogo';
import styles from './Login.module.css';

// Marketing site origin (landings + placement quiz + checkout). Pre-launch this
// is school.hauraki.ai (the temp storefront); it flips to hauraki.ai at the
// go-live cutover (docs/go-live-readiness.md §7). Overridable per-env, mirroring
// PortalLayout's MARKETING_ORIGIN so the two surfaces stay in lockstep.
const MARKETING_ORIGIN =
  process.env.REACT_APP_MARKETING_ORIGIN || 'https://school.hauraki.ai';

/**
 * ZewstID-only sign-in, laid out to Figma `01 · Auth / Login · Default`
 * (node 5:2): a dark brand story on the left and a light auth panel on the
 * right. The Figma's Google/GitHub SSO + email/password form is replaced by the
 * single ZewstID button — auth.zewstid.com is Hauraki's sole identity provider,
 * and `react-oidc-context` handles the PKCE round-trip via `signinRedirect()` →
 * `/auth/callback`. Auth behaviour is unchanged; only the presentation follows
 * the design.
 */
const Login: React.FC = () => {
  const { t } = useTranslation();
  const oidc = useAuth();
  const [search] = useSearchParams();
  const errorParam = search.get('error');
  const oidcErrorMessage = oidc.error?.message ?? null;
  const returnTo = sanitizeReturnTo(search.get('returnTo'));

  useEffect(() => {
    if (oidc.isLoading || oidc.isAuthenticated || oidc.activeNavigator) return;
    if (errorParam || oidcErrorMessage) return;
  }, [oidc.isLoading, oidc.isAuthenticated, oidc.activeNavigator, errorParam, oidcErrorMessage]);

  const handleZewstIDSignIn = () => {
    oidc.signinRedirect(returnTo ? { state: { returnTo } } : undefined).catch(() => {
      // The SDK surfaces failures through auth.error; nothing more to do here.
    });
  };

  const brandName = 'Hauraki AI';

  return (
    <div className={styles.container}>
      {/* ── Left · brand story (Figma Brand Panel) ─────────────────────── */}
      <aside className={styles.brand}>
        <div className={styles.glow} aria-hidden="true" />

        <div className={styles.brandMark}>
          <HaurakiLogo size={20} />
          <span className={styles.brandName}>{brandName}</span>
        </div>

        <div className={styles.headline}>
          <p className={styles.kicker}>FOUNDING COHORT · 2026</p>
          <h1 className={styles.title}>
            Learn AI from people who&rsquo;ve spent 30 years building it.
          </h1>
          <p className={styles.subcopy}>
            {t('ako.login_subcopy', { name: t('companion.name') })}
          </p>
        </div>

        <div className={styles.proof}>
          <div className={styles.proofItem}>
            <span className={styles.proofNum}>9</span>
            <span className={styles.proofLabel}>Certifications</span>
          </div>
          <div className={styles.proofItem}>
            <span className={styles.proofNum}>1:1</span>
            <span className={styles.proofLabel}>{t('ako.proof_label', { name: t('companion.name') })}</span>
          </div>
          <div className={styles.proofItem}>
            <span className={styles.proofNum}>Real</span>
            <span className={styles.proofLabel}>Graded work + capstone</span>
          </div>
        </div>
      </aside>

      {/* ── Right · auth panel (Figma Auth Panel) ──────────────────────── */}
      <main className={styles.auth}>
        <div className={styles.form}>
          <div className={styles.mobileBrand}>
            <HaurakiLogo size={20} />
            <span className={styles.mobileBrandName}>{brandName}</span>
          </div>

          <div className={styles.heading}>
            <h2 className={styles.welcome}>Welcome back</h2>
            <p className={styles.sub}>Sign in to continue your Hauraki path.</p>
          </div>

          <button
            type="button"
            onClick={handleZewstIDSignIn}
            disabled={oidc.isLoading}
            className={styles.primary}
          >
            {oidc.isLoading ? (
              'Connecting…'
            ) : (
              <>
                <svg
                  className={styles.buttonIcon}
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3a5 5 0 0 1 5 5v2h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1V8a5 5 0 0 1 5-5Zm3 7V8a3 3 0 1 0-6 0v2h6Z"
                    fill="currentColor"
                  />
                </svg>
                Continue with ZewstID
              </>
            )}
          </button>

          {(errorParam || oidcErrorMessage) && (
            <div className={styles.error}>
              {errorParam ? decodeURIComponent(errorParam) : oidcErrorMessage}
            </div>
          )}

          <div className={styles.secondary}>
            <span className={styles.secondaryText}>Not a student yet?</span>
            <a className={styles.quizLink} href={`${MARKETING_ORIGIN}/quiz`}>
              Take the 2-minute quiz →
            </a>
          </div>

          <p className={styles.terms}>
            By continuing you agree to the Terms and Privacy Policy.
          </p>
        </div>
      </main>
    </div>
  );
};

export default Login;
